import { getErrorMessage } from "@repo/auth/client";
import RequestResetPasswordCard from "@repo/ui/components/auth/RequestResetPasswordCard";
import SignInCard from "@repo/ui/components/auth/SignInCard";
import SignUpCard from "@repo/ui/components/auth/SignUpCard";
import { Dialog, DialogContent, DialogTitle } from "@repo/ui/components/ui/dialog";
import { VisuallyHidden } from "@repo/ui/components/ui/visually-hidden";
import { checkEmailAvailability } from "@repo/ui/lib/emailAvailability";
import { useState } from "react";
import { authClient } from "@/clients/authClient";
import { trpcClient } from "@/clients/trpcClient";
import { useLoginModal } from "@/lib/hooks/useLoginModal";
import { verificationRedirectUrl } from "@/lib/utils/verificationRedirect";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function LoginModal() {
  const {
    open,
    onOpenChange,
    onClose
  } = useLoginModal();
  const [view, setView] = useState("login");
  const [isLoading, setIsLoading] = useState(false);
  const resetView = nextOpen => {
    if (!nextOpen) setView("login");
    onOpenChange(nextOpen);
  };
  const handleLogin = async (email, password) => {
    setIsLoading(true);
    try {
      const {
        error
      } = await authClient.signIn.email({
        email,
        password
      });
      if (error) {
        return {
          success: false,
          message: getErrorMessage(error.code)
        };
      }
      onClose();
      return {
        success: true,
        message: ""
      };
    } catch {
      return {
        success: false,
        message: "Ett fel uppstod. Försök igen."
      };
    } finally {
      setIsLoading(false);
    }
  };
  const handleGoogleSignIn = async () => {
    try {
      await authClient.signIn.social({
        provider: "google",
        callbackURL: window.location.href
      });
      return {
        success: true,
        message: ""
      };
    } catch {
      return {
        success: false,
        message: "Google-inloggning misslyckades."
      };
    }
  };

  // Shared with the /skapa-konto page so both entry points answer identically.
  const handleEmailSubmit = email_0 => checkEmailAvailability(trpcClient, email_0);
  const handleSignUp = async formData => {
    setIsLoading(true);
    try {
      const {
        error: error_0
      } = await authClient.signUp.email({
        email: formData.email,
        password: formData.password,
        name: `${formData.firstName} ${formData.lastName}`,
        // Without this the verification link carries `callbackURL=/`, which
        // Better Auth resolves against the API origin — so confirming the email
        // landed the user on api.fasterforward.se instead of back on the site.
        callbackURL: verificationRedirectUrl()
      });
      if (error_0) {
        return {
          success: false,
          message: getErrorMessage(error_0.code)
        };
      }
      // SignUpCard swaps to the "check your inbox" panel on success; the
      // message is not rendered on that path.
      return {
        success: true,
        message: ""
      };
    } catch {
      return {
        success: false,
        message: "Kunde inte skapa konto."
      };
    } finally {
      setIsLoading(false);
    }
  };
  const handleResendVerification = async email_1 => {
    const {
      error: error_1
    } = await authClient.sendVerificationEmail({
      email: email_1,
      callbackURL: verificationRedirectUrl()
    });
    if (error_1) {
      return {
        success: false,
        message: getErrorMessage(error_1.code)
      };
    }
    return {
      success: true,
      message: "En ny verifieringslänk är skickad."
    };
  };
  const handleResetPassword = async email_2 => {
    setIsLoading(true);
    try {
      await authClient.requestPasswordReset({
        email: email_2,
        redirectTo: `${window.location.origin}/aterstall-losenord`
      });
      return {
        success: true,
        message: "Kontrollera din e-post för återställningslänk."
      };
    } catch {
      return {
        success: false,
        message: "Kunde inte skicka återställningslänk."
      };
    } finally {
      setIsLoading(false);
    }
  };
  const title = view === "login" ? "Logga in" : view === "register" ? "Skapa konto" : "Återställ lösenord";
  return /*#__PURE__*/_jsx(Dialog, {
    open: open,
    onOpenChange: resetView,
    children: /*#__PURE__*/_jsxs(DialogContent, {
      className: "max-w-md border-0 bg-transparent p-0 shadow-none",
      children: [/*#__PURE__*/_jsx(VisuallyHidden, {
        children: /*#__PURE__*/_jsx(DialogTitle, {
          children: title
        })
      }), view === "login" && /*#__PURE__*/_jsx(SignInCard, {
        handleLogin: handleLogin,
        signInWithGoogle: handleGoogleSignIn,
        onRegisterClick: () => setView("register"),
        onForgotPasswordClick: () => setView("forgot-password"),
        statusMessage: null,
        isLoading: isLoading
      }), view === "register" && /*#__PURE__*/_jsx(SignUpCard, {
        onEmailSubmit: handleEmailSubmit,
        onSignUp: handleSignUp,
        onResendVerification: handleResendVerification,
        onSignUpComplete: onClose,
        onBackToSignIn: () => setView("login"),
        onPrivacyPolicyClick: () => window.open("/om/integritet", "_blank"),
        onTermsClick: () => window.open("/om/villkor", "_blank"),
        loading: isLoading,
        statusMessage: null
      }), view === "forgot-password" && /*#__PURE__*/_jsx(RequestResetPasswordCard, {
        onSubmit: handleResetPassword,
        onBackToSignIn: () => setView("login"),
        loading: isLoading,
        statusMessage: null
      })]
    })
  });
}